import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Input,
  Select,
  Tag,
  Typography,
  Card,
  Form,
  Switch,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { FeatureService } from '../../services/api/featureService';
import { Feature, FeatureCreate } from '../../types/subscription';
import { SplitPaneLayout } from '../common/SplitPaneLayout';
import { ManagementForm } from '../common/ManagementForm';
import { JsonSchemaEditor } from '../Krybot/AgentManagement/JsonSchemaEditor';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifact } from '../../types/primeArtifact';
import '../../styles/management-pages.css';

const { Search, TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

interface FeatureListProps {
  onFeatureChange?: () => void;
}

export const FeatureList: React.FC<FeatureListProps> = ({ onFeatureChange }) => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [primeArtifacts, setPrimeArtifacts] = useState<PrimeArtifact[]>([]);
  const [loadingPrimeArtifacts, setLoadingPrimeArtifacts] = useState(false);

  useEffect(() => {
    loadFeatures();
    loadPrimeArtifacts();
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const featureList = await FeatureService.getAll();
      setFeatures(featureList);
    } catch (err: any) {
      message.error(err.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const loadPrimeArtifacts = async () => {
    setLoadingPrimeArtifacts(true);
    try {
      const allArtifacts = await PrimeArtifactService.getAll();
      // Filter: artifacts with parent "Krysalis Platform" or artifacts with name "Feature"
      const featureArtifacts = allArtifacts.filter((artifact) => 
        artifact.name === 'Feature' || artifact.parent_name === 'Krysalis Platform'
      );
      setPrimeArtifacts(featureArtifacts.length > 0 ? featureArtifacts : allArtifacts);
    } catch (error) {
      console.error('[FeatureList] Error fetching prime artifacts:', error);
      setPrimeArtifacts([]);
    } finally {
      setLoadingPrimeArtifacts(false);
    }
  };

  const selectedFeature = selectedFeatureId ? features.find(f => f.id === selectedFeatureId) : null;

  // Get unique categories for filter
  const categories = Array.from(new Set(features.map(f => f.category).filter(Boolean)));

  // Filter features based on search and category
  const filteredFeatures = features.filter(feature => {
    const matchesSearch = !searchText || 
      feature.code.toLowerCase().includes(searchText.toLowerCase()) ||
      feature.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (feature.description && feature.description.toLowerCase().includes(searchText.toLowerCase()));
    
    const matchesCategory = !categoryFilter || feature.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const handleSelectFeature = async (feature: Feature) => {
    setSelectedFeatureId(feature.id);
    setIsCreating(false);
    
    // Fetch the latest feature data to ensure we have the most up-to-date metadata
    try {
      const latestFeature = await FeatureService.getById(feature.id);
      if (latestFeature) {
        // Safely clone and parse metadata
        const safeClone = <T,>(obj: T | undefined | null): T | {} => {
          if (!obj) return {};
          if (typeof obj === 'string') {
            try {
              return JSON.parse(obj) as T;
            } catch {
              return {};
            }
          }
          try {
            return JSON.parse(JSON.stringify(obj)) as T;
          } catch {
            return {};
          }
        };

        const metadata = safeClone(latestFeature.metadata) as Record<string, unknown>;
        const primeArtifactId = (metadata.prime_artifact_id as string) || undefined;
        const metadataValue = Object.keys(metadata).length > 0 ? metadata : {};

        form.setFieldsValue({
          code: latestFeature.code,
          name: latestFeature.name,
          description: latestFeature.description || '',
          category: latestFeature.category || '',
          is_core_feature: latestFeature.is_core_feature || false,
          metadata: metadataValue,
          prime_artifact_id: primeArtifactId,
        });
      }
    } catch (err) {
      console.error('[FeatureList] Error fetching feature for edit:', err);
      // Fallback to feature from table
      const safeClone = <T,>(obj: T | undefined | null): T | {} => {
        if (!obj) return {};
        if (typeof obj === 'string') {
          try {
            return JSON.parse(obj) as T;
          } catch {
            return {};
          }
        }
        try {
          return JSON.parse(JSON.stringify(obj)) as T;
        } catch {
          return {};
        }
      };

      const metadata = safeClone(feature.metadata) as Record<string, unknown>;
      const primeArtifactId = (metadata.prime_artifact_id as string) || undefined;
      const metadataValue = Object.keys(metadata).length > 0 ? metadata : {};

      form.setFieldsValue({
        code: feature.code,
        name: feature.name,
        description: feature.description || '',
        category: feature.category || '',
        is_core_feature: feature.is_core_feature || false,
        metadata: metadataValue,
        prime_artifact_id: primeArtifactId,
      });
    }
  };

  const handleCreateFeature = () => {
    const tempId = `temp-${Date.now()}`;
    const newFeature: Feature = {
      id: tempId,
      code: '',
      name: '',
      category: '',
      is_core_feature: false,
    };
    setFeatures([...features, newFeature]);
    setSelectedFeatureId(tempId);
    setIsCreating(true);
    form.resetFields();
    form.setFieldsValue({
      is_core_feature: false,
      metadata: {},
    });
  };

  const handleSave = async (values: any) => {
    setIsSaving(true);
    try {
      // Handle metadata - can be object (from JsonSchemaEditor) or string (legacy)
      let metadata = values.metadata;
      if (typeof metadata === 'string' && metadata.trim()) {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          message.error('Invalid JSON format in metadata');
          setIsSaving(false);
          return;
        }
      }

      // If prime artifact is selected, ensure it's in metadata
      if (values.prime_artifact_id) {
        metadata = metadata || {};
        metadata.prime_artifact_id = values.prime_artifact_id;
      } else if (metadata && metadata.prime_artifact_id) {
        // Remove prime_artifact_id if deselected
        const { prime_artifact_id, ...rest } = metadata;
        metadata = Object.keys(rest).length > 0 ? rest : undefined;
      }

      if (isCreating && selectedFeatureId?.startsWith('temp-')) {
        // Create new feature
        const createData: any = {
          feature_code: values.code.trim().toUpperCase(),
          feature_name: values.name.trim(),
          is_core_feature: values.is_core_feature || false,
        };

        if (values.description) {
          createData.description = values.description.trim();
        }
        if (values.category) {
          createData.feature_category = values.category.trim();
        }
        if (metadata && Object.keys(metadata).length > 0) {
          createData.metadata = metadata;
        }

        const newFeature = await FeatureService.createWithOptimisticCheck(createData as FeatureCreate);
        message.success('Feature created successfully');
        
        // Remove temp item and add real one
        setFeatures(features.filter(f => f.id !== selectedFeatureId).concat(newFeature));
        setSelectedFeatureId(newFeature.id);
        setIsCreating(false);
        onFeatureChange?.();
      } else if (selectedFeature) {
        // Update existing feature
        const updateData: FeatureCreate = {
          feature_code: selectedFeature.code,
          feature_name: values.name.trim(),
          feature_category: values.category || selectedFeature.category || '',
          description: values.description?.trim() || undefined,
          is_core_feature: values.is_core_feature !== undefined ? values.is_core_feature : selectedFeature.is_core_feature || false,
          metadata: metadata && Object.keys(metadata).length > 0 ? metadata : undefined,
        };

        const updatedFeature = await FeatureService.update(selectedFeature.id, updateData);
        message.success('Feature updated successfully');
        
        // Update in list
        setFeatures(features.map(f => f.id === selectedFeature.id ? updatedFeature : f));
        setSelectedFeatureId(updatedFeature.id);
        onFeatureChange?.();
      }
      
      await loadFeatures();
    } catch (error: any) {
      console.error('Error saving feature:', error);
      
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail
          .map((err: any) => {
            if (typeof err === 'string') return err;
            return `${err.loc?.join('.')}: ${err.msg || err.message || 'Validation error'}`;
          })
          .join(', ');
        message.error(`Validation error: ${errorMessages}`);
      } else {
        const detail = error.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : error.message || 'Failed to save feature';
        message.error(errorMsg);
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreating && selectedFeatureId?.startsWith('temp-')) {
      // Remove temp item from list
      setFeatures(features.filter(f => f.id !== selectedFeatureId));
    }
    setSelectedFeatureId(null);
    setIsCreating(false);
    form.resetFields();
  };

  const handleDelete = async () => {
    if (!selectedFeature) return;
    
    try {
      await FeatureService.delete(selectedFeature.id);
      message.success('Feature deleted successfully');
      setSelectedFeatureId(null);
      setIsCreating(false);
      await loadFeatures();
      onFeatureChange?.();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete feature');
      throw error;
    }
  };

  const handleDeleteFromTable = async (id: string) => {
    try {
      await FeatureService.delete(id);
      message.success('Feature deleted successfully');
      if (selectedFeatureId === id) {
        setSelectedFeatureId(null);
        setIsCreating(false);
      }
      await loadFeatures();
      onFeatureChange?.();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete feature');
    }
  };

  // Handle prime artifact selection - sync with metadata
  const handlePrimeArtifactChange = (value: string | undefined) => {
    const currentMetadata = form.getFieldValue('metadata') || {};
    if (value) {
      form.setFieldsValue({
        metadata: { ...currentMetadata, prime_artifact_id: value },
      });
    } else {
      const { prime_artifact_id, ...rest } = currentMetadata;
      form.setFieldsValue({
        metadata: Object.keys(rest).length > 0 ? rest : undefined,
      });
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text: string, feature: Feature) => (
        <Button
          type="link"
          onClick={() => handleSelectFeature(feature)}
          style={{ padding: 0 }}
        >
          <Text strong>{text}</Text>
        </Button>
      ),
      sorter: (a: Feature, b: Feature) => a.code.localeCompare(b.code),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, feature: Feature) => (
        <Button
          type="link"
          onClick={() => handleSelectFeature(feature)}
          style={{ padding: 0 }}
        >
          <Text>{text}</Text>
        </Button>
      ),
      sorter: (a: Feature, b: Feature) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string | undefined) => category ? <Tag>{category}</Tag> : '-',
    },
    {
      title: 'Core Feature',
      dataIndex: 'is_core_feature',
      key: 'is_core_feature',
      render: (isCore: boolean | undefined) => isCore ? <Tag color="blue">Core</Tag> : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, feature: Feature) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleSelectFeature(feature)}
            title="Edit feature"
          />
          <Popconfirm
            title="Are you sure you want to delete this feature?"
            description="This action cannot be undone. Make sure this feature is not used by any subscription types."
            onConfirm={() => handleDeleteFromTable(feature.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete feature"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="management-page-container">
      <div className="management-page-header">
        <Title level={2}>Features</Title>
        <Text type="secondary">Manage feature definitions</Text>
      </div>

      <SplitPaneLayout
        leftContent={
          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                <Space wrap>
                  <Search
                    placeholder="Search features..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Select
                    placeholder="Filter by category"
                    allowClear
                    style={{ width: 200 }}
                    onChange={setCategoryFilter}
                    value={categoryFilter}
                  >
                    {categories.map(cat => (
                      <Option key={cat} value={cat}>{cat}</Option>
                    ))}
                  </Select>
                </Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateFeature}
                >
                  New
                </Button>
              </Space>

              <Table
                columns={columns}
                dataSource={filteredFeatures}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} features`,
                }}
                rowClassName={(record) => 
                  record.id === selectedFeatureId ? 'ant-table-row-selected' : ''
                }
                onRow={(record) => ({
                  onClick: () => handleSelectFeature(record),
                  style: { cursor: 'pointer' },
                })}
              />
            </Space>
          </Card>
        }
        rightContent={
          selectedFeatureId ? (
            <ManagementForm
              title={isCreating ? 'Create Feature' : `Edit: ${selectedFeature?.code || ''}`}
              isCreating={isCreating}
              isLoading={false}
              isSaving={isSaving}
              form={form}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
              showDelete={!isCreating}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Form.Item
                  name="code"
                  label="Feature Code"
                  rules={[
                    { required: true, message: 'Please enter feature code' },
                    { pattern: /^[A-Z_][A-Z0-9_]*$/, message: 'Code must be uppercase with underscores (e.g., PLATFORM_ARTIFACT_MODELING)' },
                  ]}
                >
                  <Input 
                    placeholder="e.g., PLATFORM_ARTIFACT_MODELING" 
                    disabled={!isCreating}
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Item>

                <Form.Item
                  name="name"
                  label="Feature Name"
                  rules={[{ required: true, message: 'Please enter feature name' }]}
                >
                  <Input placeholder="e.g., Platform Artifact Modeling" />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                >
                  <TextArea
                    rows={3}
                    placeholder="Enter feature description"
                  />
                </Form.Item>

                <Form.Item
                  name="category"
                  label="Category"
                >
                  <Input placeholder="e.g., platform, core, fitfinder, marketfit, client" />
                </Form.Item>

                <Form.Item
                  name="is_core_feature"
                  label="Core Feature"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  name="prime_artifact_id"
                  label="Prime Artifact Reference"
                  tooltip="Select the prime artifact that defines this feature (stored in metadata.prime_artifact_id)"
                >
                  <Select
                    placeholder="Select prime artifact (optional)"
                    allowClear
                    showSearch
                    loading={loadingPrimeArtifacts}
                    filterOption={(input, option) =>
                      (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
                    }
                    onChange={handlePrimeArtifactChange}
                    options={primeArtifacts.map(artifact => ({
                      value: artifact.id,
                      label: artifact.name,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="metadata"
                  label="Metadata (JSON)"
                  tooltip="Additional metadata for the feature"
                >
                  <JsonSchemaEditor
                    key={`metadata-${selectedFeatureId || 'new'}`}
                    fieldType="input_schema"
                    placeholder='{"version": "1.0.0", "documentation_link": "docs/features/FEATURE_CODE.md", "capabilities": [], "dependencies": []}'
                    description="Feature metadata in JSON format. Use code mode to edit directly."
                  />
                </Form.Item>
              </Space>
            </ManagementForm>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
                <Text>Select a feature from the list to view or edit</Text>
              </div>
            </Card>
          )
        }
      />
    </div>
  );
};
